import React, { useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Divider, FormControlLabel, Stack, Typography,
} from '@mui/material';
import ConfirmDialog from './ConfirmDialog';
import StatusChip, { DryRunChip } from './StatusChip';
import useCatalog from '../viewmodels/useCatalog';
import { formatDateTime } from '../utils/format';

const TITLE = {
  send_email: 'Send email',
  create_team: 'Create a team',
  create_bot: 'Add a team member',
  update_reporting: 'Change the reporting lines',
  create_schedule: 'Start a schedule',
};

const APPROVE_LABEL = {
  send_email: 'Approve',
  create_team: 'Create team',
  create_bot: 'Add team member',
  update_reporting: 'Apply changes',
  create_schedule: 'Start schedule',
};

function Field({ label, children }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {children}
    </Box>
  );
}

function EmailBody({ payload }) {
  return (
    <>
      <Field label="To"><Typography>{payload.to}</Typography></Field>
      <Field label="Subject"><Typography>{payload.subject}</Typography></Field>
      <Field label="Message">
        <Box sx={{ whiteSpace: 'pre-wrap', bgcolor: 'action.hover', borderRadius: 2, p: 1.5, mt: 0.5 }}>{payload.body}</Box>
      </Field>
    </>
  );
}

function TeamBody({ payload }) {
  return (
    <>
      <Typography sx={{ mb: 1 }}>
        {`This adds ${payload.members.length} team members to your ${payload.title}. Each starts with only the access shown.`}
      </Typography>
      <Stack spacing={1}>
        {payload.members.map((m) => (
          <Box key={m.name} sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.25 }}>
            <Typography fontWeight={600}>
              {m.name}
              <Typography component="span" color="text.secondary">{` · ${m.role}`}</Typography>
              {!m.active && <Chip size="small" label="inactive for now" sx={{ ml: 1 }} />}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`Reports to ${m.reports_to}. Access: ${m.tools.length ? m.tools.join(', ') : 'none'}.`}
            </Typography>
          </Box>
        ))}
      </Stack>
    </>
  );
}

function ScheduleBody({ payload }) {
  return (
    <>
      <Field label="Name"><Typography>{payload.title}</Typography></Field>
      <Field label="When"><Typography>{`${payload.description} (${payload.timezone})`}</Typography></Field>
      <Field label="Sent to"><Typography>{payload.conversation_title}</Typography></Field>
      <Field label="Message">
        <Box sx={{ whiteSpace: 'pre-wrap', bgcolor: 'action.hover', borderRadius: 2, p: 1.5, mt: 0.5 }}>{payload.prompt}</Box>
      </Field>
      <Typography variant="body2" color="text.secondary">Each run uses your AI key. Automatic runs are limited per day.</Typography>
    </>
  );
}

function BotBody({ payload, catalog, grants, setGrants, editable }) {
  const [open, setOpen] = useState(false);
  const toggle = (action) => setGrants((g) => (g.includes(action) ? g.filter((a) => a !== action) : [...g, action]));
  return (
    <>
      <Field label="Name and role"><Typography>{`${payload.name} · ${payload.role}`}</Typography></Field>
      <Field label="Reports to"><Typography>{payload.reports_to_name}</Typography></Field>
      <Field label="What they do">
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
          {open || payload.instructions.length < 160 ? payload.instructions : `${payload.instructions.slice(0, 160)}...`}
        </Typography>
        {payload.instructions.length >= 160 && (
          <Button size="small" sx={{ px: 0 }} onClick={() => setOpen((o) => !o)}>{open ? 'Show less' : 'Show all'}</Button>
        )}
      </Field>
      <Field label="Access (starts with none; you decide)">
        <Stack>
          {catalog.map((c) => (
            <FormControlLabel
              key={c.action}
              disabled={!editable}
              control={<Checkbox size="small" checked={grants.includes(c.action)} onChange={() => toggle(c.action)} />}
              label={(
                <Typography variant="body2">
                  {c.label}
                  {payload.suggested_tools.includes(c.action) && <Chip size="small" color="primary" variant="outlined" label="suggested" sx={{ ml: 1 }} />}
                </Typography>
              )}
            />
          ))}
        </Stack>
      </Field>
    </>
  );
}

function ReportingBody({ payload }) {
  return (
    <Field label="Changes, applied in this order">
      <Stack spacing={0.5}>
        {(payload.changes || []).map((c) => (
          <Typography key={`${c.bot_id}-${c.to_id}`} variant="body2">
            <strong>{c.bot_name}</strong>
            {` : ${c.from_name || 'unassigned'} → ${c.to_name || 'unassigned'}`}
          </Typography>
        ))}
      </Stack>
    </Field>
  );
}

function outcomeText(a) {
  if (a.status === 'pending') return `Proposed ${formatDateTime(a.created_at)}. Expires ${formatDateTime(a.expires_at)}.`;
  if (a.status === 'rejected') return `Rejected on ${formatDateTime(a.decided_at)}. Nothing was changed or sent.`;
  if (a.status === 'expired') return 'This request expired and can no longer be approved.';
  if (a.status === 'failed') return `Approved, but it could not be completed: ${a.result?.error || 'unknown error'}.`;
  if (a.status === 'executed') {
    if (a.type === 'send_email') return `Approved and run as a dry run on ${formatDateTime(a.executed_at)}. No email was sent.`;
    if (a.type === 'create_team') return `Done on ${formatDateTime(a.executed_at)}: ${(a.result?.created || []).join(', ') || 'nothing new to add'}.`;
    if (a.type === 'update_reporting') return `Done on ${formatDateTime(a.executed_at)}: ${(a.result?.changed || []).join('; ')}.`;
    if (a.type === 'create_schedule') return `Started on ${formatDateTime(a.executed_at)}. ${a.result?.description || ''}`.trim();
    if (a.type === 'create_bot') {
      const g = a.result?.granted || [];
      return `Added on ${formatDateTime(a.executed_at)}. Access granted: ${g.length ? g.join(', ') : 'none'}.`;
    }
  }
  return '';
}

/** One approval: what is proposed, its state, and Approve and Reject for the owner. Used in chat cards and the Approvals page. */
export default function ApprovalContent({
  approval, isOwner, busy, onApprove, onReject, framed = false,
}) {
  const catalog = useCatalog();
  const [grants, setGrants] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const p = approval.payload || {};
  const pending = approval.status === 'pending';

  const approve = () => {
    if (approval.type === 'send_email') { setConfirm('approve'); return; }
    onApprove(approval.type === 'create_bot' ? grants : undefined);
  };

  return (
    <Box sx={framed ? { border: 1, borderColor: 'divider', borderRadius: 3, p: 2, bgcolor: 'background.paper' } : undefined}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
        <Typography variant="subtitle1" fontWeight={600}>{TITLE[approval.type] || approval.type}</Typography>
        <Stack direction="row" spacing={1}>
          {approval.type === 'send_email' && <DryRunChip />}
          <StatusChip status={approval.status} />
        </Stack>
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      {approval.type === 'send_email' && <EmailBody payload={p} />}
      {approval.type === 'create_team' && <TeamBody payload={p} />}
      {approval.type === 'create_schedule' && <ScheduleBody payload={p} />}
      {approval.type === 'update_reporting' && <ReportingBody payload={p} />}
      {approval.type === 'create_bot' && (
        <BotBody payload={p} catalog={catalog} grants={grants} setGrants={setGrants} editable={pending && isOwner} />
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>{outcomeText(approval)}</Typography>
      {pending && (
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="contained" disabled={!isOwner || busy} onClick={approve}>{APPROVE_LABEL[approval.type] || 'Approve'}</Button>
          <Button color="inherit" disabled={!isOwner || busy} onClick={onReject}>Reject</Button>
          {!isOwner && <Typography variant="body2" color="text.secondary">Only the account owner can decide.</Typography>}
        </Stack>
      )}
      <ConfirmDialog
        open={confirm === 'approve'}
        title="Approve this email?"
        body={`This is a dry run. The email to ${p.to} will be recorded as sent, but no email will actually be sent.`}
        confirmLabel="Approve"
        onConfirm={() => { setConfirm(null); onApprove(); }}
        onClose={() => setConfirm(null)}
      />
    </Box>
  );
}
